import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

const emptyForm = { name: '', weight_grams: '', package_format: 'Parcel', height_mm: '', width_mm: '', depth_mm: '', value_gbp: '' };

const PACKAGE_FORMATS = ['Parcel', 'largeLetter', 'smallParcel', 'mediumParcel'];

/**
 * Admin-only: create/edit/delete named weight+dimensions presets ("Glasses
 * -- standard case", "Repair kit", etc.) that staff pick from a dropdown
 * on the "Generate shipment" button instead of typing a weight by hand on
 * every single Royal Mail shipment -- see TicketDetailModal's Shipping
 * section and tickets/royal_mail.py.
 */
const ShippingProfileManagerModal = ({ onClose }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.getShippingProfiles()
      .then(setProfiles)
      .catch(() => toast.error('Could not load shipping profiles.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.weight_grams) {
      toast.error('Name and weight are both required.');
      return;
    }
    try {
      setCreating(true);
      await api.createShippingProfile({
        ...form,
        height_mm: form.height_mm || null,
        width_mm: form.width_mm || null,
        depth_mm: form.depth_mm || null,
        value_gbp: form.value_gbp || null,
      });
      setForm(emptyForm);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not create shipping profile.');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (profile) => {
    setEditingId(profile.id);
    setEditForm({
      name: profile.name,
      weight_grams: profile.weight_grams,
      package_format: profile.package_format,
      height_mm: profile.height_mm || '',
      width_mm: profile.width_mm || '',
      depth_mm: profile.depth_mm || '',
      value_gbp: profile.value_gbp || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim() || !editForm.weight_grams) {
      toast.error('Name and weight cannot be blank.');
      return;
    }
    try {
      setSaving(true);
      await api.updateShippingProfile(editingId, {
        ...editForm,
        height_mm: editForm.height_mm || null,
        width_mm: editForm.width_mm || null,
        depth_mm: editForm.depth_mm || null,
        value_gbp: editForm.value_gbp || null,
      });
      setEditingId(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not update shipping profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profileId) => {
    if (!window.confirm('Delete this shipping profile? Tickets that already shipped with it keep their tracking number and label -- only the preset itself goes away.')) return;
    try {
      await api.deleteShippingProfile(profileId);
      if (editingId === profileId) setEditingId(null);
      load();
    } catch (error) {
      toast.error('Could not delete shipping profile.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0 }}>Shipping profiles</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#aab7c4' }}>
          Named weight/dimension presets for the "Generate shipment" button on a ticket -- pick one from a
          dropdown there instead of typing a weight by hand every time. Dimensions are optional.
        </p>

        {loading ? <p>Loading...</p> : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1rem' }}>
              {profiles.length === 0 && <p style={{ color: '#aab7c4' }}>No shipping profiles yet.</p>}
              {profiles.map((profile) => (
                <div key={profile.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.65rem 0.75rem' }}>
                  {editingId === profile.id ? (
                    <>
                      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                        <input className="form-input" style={{ flex: 2 }} value={editForm.name}
                          onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} placeholder="Name" />
                        <input className="form-input" type="number" style={{ flex: 1 }} value={editForm.weight_grams}
                          onChange={(e) => setEditForm((p) => ({ ...p, weight_grams: e.target.value }))} placeholder="Weight (g)" />
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                        <select className="form-select" style={{ flex: 1 }} value={editForm.package_format}
                          onChange={(e) => setEditForm((p) => ({ ...p, package_format: e.target.value }))}>
                          {PACKAGE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                        <input className="form-input" type="number" style={{ flex: 1 }} value={editForm.height_mm}
                          onChange={(e) => setEditForm((p) => ({ ...p, height_mm: e.target.value }))} placeholder="Height (mm)" />
                        <input className="form-input" type="number" style={{ flex: 1 }} value={editForm.width_mm}
                          onChange={(e) => setEditForm((p) => ({ ...p, width_mm: e.target.value }))} placeholder="Width (mm)" />
                        <input className="form-input" type="number" style={{ flex: 1 }} value={editForm.depth_mm}
                          onChange={(e) => setEditForm((p) => ({ ...p, depth_mm: e.target.value }))} placeholder="Depth (mm)" />
                        <input className="form-input" type="number" style={{ flex: 1 }} value={editForm.value_gbp}
                          onChange={(e) => setEditForm((p) => ({ ...p, value_gbp: e.target.value }))} placeholder="Value (£)" />
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button type="button" className="btn-primary" onClick={handleSaveEdit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                        <button type="button" className="btn-secondary" onClick={() => setEditingId(null)} disabled={saving}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>{profile.name}</strong>
                        <p style={{ fontSize: '0.78rem', color: '#aab7c4', margin: '0.2rem 0 0' }}>
                          {profile.weight_grams}g &middot; {profile.package_format}
                          {profile.height_mm && profile.width_mm && profile.depth_mm
                            ? ` — ${profile.height_mm}×${profile.width_mm}×${profile.depth_mm}mm` : ''}
                          {profile.value_gbp ? ` — £${Number(profile.value_gbp).toFixed(2)}` : ''}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button type="button" className="btn-secondary" onClick={() => startEdit(profile)}>Edit</button>
                        <button type="button" className="btn-danger" onClick={() => handleDelete(profile.id)}>Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="section-title">New shipping profile</div>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                <input className="form-input" style={{ flex: 2 }} value={form.name} disabled={creating}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Name, e.g. 'Glasses -- standard case'" />
                <input className="form-input" type="number" style={{ flex: 1 }} value={form.weight_grams} disabled={creating}
                  onChange={(e) => setForm((p) => ({ ...p, weight_grams: e.target.value }))} placeholder="Weight (g)" />
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.4rem' }}>
                <select className="form-select" style={{ flex: 1 }} value={form.package_format} disabled={creating}
                  onChange={(e) => setForm((p) => ({ ...p, package_format: e.target.value }))}>
                  {PACKAGE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <input className="form-input" type="number" style={{ flex: 1 }} value={form.height_mm} disabled={creating}
                  onChange={(e) => setForm((p) => ({ ...p, height_mm: e.target.value }))} placeholder="Height (mm)" />
                <input className="form-input" type="number" style={{ flex: 1 }} value={form.width_mm} disabled={creating}
                  onChange={(e) => setForm((p) => ({ ...p, width_mm: e.target.value }))} placeholder="Width (mm)" />
                <input className="form-input" type="number" style={{ flex: 1 }} value={form.depth_mm} disabled={creating}
                  onChange={(e) => setForm((p) => ({ ...p, depth_mm: e.target.value }))} placeholder="Depth (mm)" />
                <input className="form-input" type="number" style={{ flex: 1 }} value={form.value_gbp} disabled={creating}
                  onChange={(e) => setForm((p) => ({ ...p, value_gbp: e.target.value }))} placeholder="Value (£)" />
              </div>
              <p style={{ fontSize: '0.75rem', color: '#aab7c4', margin: '-0.1rem 0 0.4rem' }}>
                Dimensions are optional -- leave blank if you only want to preset the weight. Value defaults to £150 if left blank.
              </p>
              <button type="submit" className="btn-primary" disabled={creating}>{creating ? 'Creating...' : 'Create profile'}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ShippingProfileManagerModal;
